import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import api from "../lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import {
  Upload, ScanSearch, Loader2, FileText, Trash, Plus, Truck, AlertCircle,
  Sparkles, ChevronRight, CheckCircle2,
} from "lucide-react";

const STEP_UPLOAD = "upload";
const STEP_REVIEW = "review";

const emptyLine = () => ({
  _key: crypto.randomUUID(),
  sku: "",
  item_name: "",
  expected_quantity: "",
  unit: "each",
});

export default function OffSitePaperworkDialog({ open, onOpenChange, onSuccess }) {
  const [step, setStep] = useState(STEP_UPLOAD);
  const [projects, setProjects] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [attachment, setAttachment] = useState(null); // {id, original_filename, ...}
  const [suggestion, setSuggestion] = useState(null); // {project_id, name, confidence}
  const [projectId, setProjectId] = useState("");
  const [reference, setReference] = useState("");
  const [vendor, setVendor] = useState("");
  const [expectedAt, setExpectedAt] = useState("");
  const [deliveryLocation, setDeliveryLocation] = useState("");
  const [note, setNote] = useState("");
  const [lines, setLines] = useState([]);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    if (open) {
      // Reset state on open
      setStep(STEP_UPLOAD);
      setAttachment(null);
      setSuggestion(null);
      setProjectId("");
      setReference("");
      setVendor("");
      setExpectedAt("");
      setDeliveryLocation("");
      setNote("");
      setLines([]);
      api.get("/projects", { params: { archived: false } })
        .then((r) => setProjects(r.data))
        .catch(() => {});
    }
  }, [open]);

  const chooseFile = () => fileRef.current?.click();

  const handleFile = async (file) => {
    if (!file) return;
    if (file.size > 20 * 1024 * 1024) {
      toast.error("File exceeds 20 MB");
      return;
    }
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post("/offsite-deliveries/extract-paperwork", form, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 120000,
      });
      // Populate review form from OCR
      setAttachment(data.attachment);
      setSuggestion(data.project_suggestion || null);
      // Do not auto-assign — user must confirm per requirement
      setProjectId("");
      const ex = data.extract || {};
      setReference(ex.po_number || "");
      setVendor(ex.vendor || "");
      setExpectedAt((ex.expected_at || "").slice(0, 10));
      setDeliveryLocation(ex.delivery_location || "");
      setNote(ex.note || "");
      const rows = (ex.lines || []).map((l) => ({
        _key: crypto.randomUUID(),
        sku: (l.sku || "").toString().trim().toUpperCase(),
        item_name: (l.item_name || l.name || "").toString().trim(),
        expected_quantity: String(l.expected_quantity ?? l.quantity ?? ""),
        unit: (l.unit || "each").toString().toLowerCase(),
      }));
      setLines(rows.length > 0 ? rows : [emptyLine()]);
      setStep(STEP_REVIEW);
      toast.success(
        `Scanned ${data.pages_scanned} page(s) · ${rows.length} line${rows.length === 1 ? "" : "s"} extracted`
      );
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not read the paperwork");
    } finally {
      setUploading(false);
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    if (e.dataTransfer.files?.[0]) handleFile(e.dataTransfer.files[0]);
  };

  const updateLine = (i, field, value) =>
    setLines((ls) => ls.map((l, idx) => (idx === i ? { ...l, [field]: value } : l)));
  const addLine = () => setLines((ls) => [...ls, emptyLine()]);
  const removeLine = (i) => setLines((ls) => ls.filter((_, idx) => idx !== i));

  const validLines = lines.filter(
    (l) => l.sku.trim() && Number(l.expected_quantity) > 0
  );
  const total = validLines.reduce((a, l) => a + Number(l.expected_quantity || 0), 0);

  const confirm = async () => {
    if (!projectId) {
      toast.error("Choose a project before saving");
      return;
    }
    if (validLines.length === 0) {
      toast.error("Add at least one line with a SKU and quantity");
      return;
    }
    setSaving(true);
    try {
      await api.post("/expected-deliveries", {
        project_id: projectId,
        reference: reference.trim(),
        vendor: vendor.trim(),
        expected_at: expectedAt || null,
        delivery_location: deliveryLocation.trim(),
        note: note.trim(),
        lines: validLines.map((l) => ({
          sku: l.sku.trim(),
          expected_quantity: Number(l.expected_quantity) || 0,
          note: l.item_name || "",
        })),
        paperwork_ids: attachment ? [attachment.id] : [],
      });
      toast.success("Off-site delivery created");
      onSuccess?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not create delivery");
    } finally {
      setSaving(false);
    }
  };

  const useSuggestion = () => {
    if (suggestion?.project_id) setProjectId(suggestion.project_id);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl rounded-md" data-testid="offsite-paperwork-dialog">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className="grid place-items-center h-10 w-10 rounded-sm bg-blue-100 text-blue-800">
              <ScanSearch size={20} />
            </div>
            <div>
              <DialogTitle className="font-display text-2xl font-black tracking-tight">
                {step === STEP_UPLOAD ? "Scan Delivery Paperwork" : "Review Extracted Delivery"}
              </DialogTitle>
              <DialogDescription className="text-slate-600 mt-1">
                {step === STEP_UPLOAD
                  ? "Upload the PO, packing list, vendor confirmation, or delivery manifest. We'll read it and prefill an expected off-site delivery for you to confirm."
                  : "Fix anything the scanner got wrong, choose the project, then confirm to open this delivery for receiving."}
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        {step === STEP_UPLOAD && (
          <div className="space-y-4">
            <div
              onDragOver={(e) => e.preventDefault()}
              onDrop={onDrop}
              onClick={chooseFile}
              className="border-2 border-dashed border-slate-300 rounded-md p-10 text-center cursor-pointer hover:border-blue-400 hover:bg-blue-50/40 transition-colors"
              data-testid="paperwork-dropzone"
            >
              {uploading ? (
                <>
                  <Loader2 size={40} className="mx-auto text-blue-800 animate-spin" />
                  <div className="mt-3 font-display text-lg font-bold text-slate-900">Reading paperwork…</div>
                  <p className="mt-1 text-sm text-slate-600">
                    The vision model is extracting vendor, PO number, delivery date and line items.
                  </p>
                </>
              ) : (
                <>
                  <Upload size={40} className="mx-auto text-slate-400" />
                  <div className="mt-3 font-display text-lg font-bold text-slate-900">
                    Drop a file or tap to select
                  </div>
                  <p className="mt-1 text-sm text-slate-600">
                    PDF, JPG, PNG, WEBP or HEIC. Multi-page PDFs are supported.
                  </p>
                </>
              )}
              <input
                ref={fileRef}
                type="file"
                accept=".pdf,image/*"
                className="hidden"
                data-testid="paperwork-file-input"
                onChange={(e) => handleFile(e.target.files?.[0])}
              />
            </div>
          </div>
        )}

        {step === STEP_REVIEW && (
          <div className="space-y-5 max-h-[65vh] overflow-y-auto pr-1">
            {attachment && (
              <div className="flex items-center gap-3 bg-slate-50 border border-slate-200 rounded-sm px-3 py-2">
                <FileText size={16} className="text-slate-500" />
                <span className="text-sm font-mono text-slate-700 truncate flex-1">
                  {attachment.original_filename}
                </span>
                <span className="text-[10px] uppercase tracking-widest font-bold text-slate-500">
                  Attached
                </span>
              </div>
            )}

            {/* Project — required, with suggestion */}
            <div>
              <Label className="text-xs font-bold uppercase tracking-widest text-slate-500">
                Project (required)
              </Label>
              {suggestion && projectId !== suggestion.project_id && (
                <button
                  type="button"
                  onClick={useSuggestion}
                  className="mt-1.5 w-full text-left border border-blue-200 bg-blue-50 rounded-sm px-3 py-2 hover:bg-blue-100 flex items-center gap-2"
                  data-testid="project-suggestion"
                >
                  <Sparkles size={16} className="text-blue-800 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="text-xs text-blue-900">
                      Suggested from address:{" "}
                      <span className="font-semibold">{suggestion.name}</span>
                      {suggestion.location && (
                        <span className="text-blue-700"> · {suggestion.location}</span>
                      )}
                    </div>
                    <div className="text-[10px] text-blue-700 mt-0.5">
                      Tap to use this project · you can still pick a different one below
                    </div>
                  </div>
                  <ChevronRight size={14} className="text-blue-800" />
                </button>
              )}
              <Select value={projectId} onValueChange={setProjectId}>
                <SelectTrigger data-testid="review-project-select" className="mt-1.5 rounded-sm">
                  <SelectValue placeholder="Choose the project this delivery is for" />
                </SelectTrigger>
                <SelectContent>
                  {projects.map((p) => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {!suggestion && deliveryLocation && (
                <div className="mt-1 flex items-center gap-1 text-xs text-slate-500">
                  <AlertCircle size={11} /> Couldn't confidently match a project — please pick manually.
                </div>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-xs font-bold uppercase tracking-widest text-slate-500">Vendor</Label>
                <Input
                  value={vendor}
                  onChange={(e) => setVendor(e.target.value)}
                  data-testid="review-vendor"
                  className="mt-1.5 rounded-sm"
                />
              </div>
              <div>
                <Label className="text-xs font-bold uppercase tracking-widest text-slate-500">PO Number / Reference</Label>
                <Input
                  value={reference}
                  onChange={(e) => setReference(e.target.value)}
                  data-testid="review-reference"
                  className="mt-1.5 rounded-sm font-mono"
                />
              </div>
              <div>
                <Label className="text-xs font-bold uppercase tracking-widest text-slate-500">Expected Delivery Date</Label>
                <Input
                  type="date"
                  value={expectedAt}
                  onChange={(e) => setExpectedAt(e.target.value)}
                  data-testid="review-expected-at"
                  className="mt-1.5 rounded-sm"
                />
              </div>
              <div>
                <Label className="text-xs font-bold uppercase tracking-widest text-slate-500">Delivery Location</Label>
                <Input
                  value={deliveryLocation}
                  onChange={(e) => setDeliveryLocation(e.target.value)}
                  data-testid="review-location"
                  className="mt-1.5 rounded-sm"
                />
              </div>
            </div>

            <div>
              <Label className="text-xs font-bold uppercase tracking-widest text-slate-500">Delivery Note</Label>
              <Textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                rows={2}
                data-testid="review-note"
                className="mt-1.5 rounded-sm"
              />
            </div>

            {/* Lines */}
            <div className="border border-slate-200 rounded-md overflow-hidden">
              <div className="flex items-center justify-between bg-slate-50 border-b border-slate-200 p-3">
                <div className="text-xs font-bold uppercase tracking-widest text-slate-500">
                  Line Items ({validLines.length}) · Total {total}
                </div>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={addLine}
                  data-testid="review-add-line"
                  className="rounded-sm border-slate-300"
                >
                  <Plus size={14} className="mr-1.5" /> Add
                </Button>
              </div>
              <div className="divide-y divide-slate-200 max-h-[35vh] overflow-y-auto">
                {lines.map((l, i) => (
                  <div key={l._key} className="p-3 grid grid-cols-12 gap-2 items-end">
                    <div className="col-span-3">
                      {i === 0 && (
                        <Label className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Part / SKU</Label>
                      )}
                      <Input
                        value={l.sku}
                        onChange={(e) => updateLine(i, "sku", e.target.value)}
                        data-testid={`review-sku-${i}`}
                        className="rounded-sm mt-1 font-mono uppercase"
                      />
                    </div>
                    <div className="col-span-6">
                      {i === 0 && (
                        <Label className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Description</Label>
                      )}
                      <Input
                        value={l.item_name}
                        onChange={(e) => updateLine(i, "item_name", e.target.value)}
                        data-testid={`review-name-${i}`}
                        className="rounded-sm mt-1"
                      />
                    </div>
                    <div className="col-span-2">
                      {i === 0 && (
                        <Label className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Qty</Label>
                      )}
                      <Input
                        type="number"
                        min="0"
                        value={l.expected_quantity}
                        onChange={(e) => updateLine(i, "expected_quantity", e.target.value)}
                        data-testid={`review-qty-${i}`}
                        className="rounded-sm mt-1 font-mono"
                      />
                    </div>
                    <div className="col-span-1 flex justify-end">
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => removeLine(i)}
                        disabled={lines.length === 1}
                        aria-label="Remove line"
                        data-testid={`review-remove-${i}`}
                        className="h-9 w-9 rounded-sm text-slate-500 hover:text-red-600"
                      >
                        <Trash size={14} />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        <DialogFooter>
          {step === STEP_UPLOAD && (
            <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-sm">
              Cancel
            </Button>
          )}
          {step === STEP_REVIEW && (
            <>
              <Button
                variant="outline"
                onClick={() => setStep(STEP_UPLOAD)}
                className="rounded-sm"
                data-testid="review-back-btn"
              >
                Back
              </Button>
              <Button
                onClick={confirm}
                disabled={saving || validLines.length === 0 || !projectId}
                data-testid="review-confirm-btn"
                className="bg-blue-800 hover:bg-blue-900 text-white rounded-sm"
              >
                {saving ? <Loader2 size={16} className="animate-spin mr-1.5" /> : <CheckCircle2 size={16} className="mr-1.5" />}
                Confirm & Open Delivery
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
